import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface PendingInvite {
  email: string;
  role: 'admin' | 'member';
}

interface BrandingData {
  primaryColor: string;
  industry: string;
  description: string;
}

interface OnboardingState {
  currentStep: number;
  currentSubStep: number;
  workspaceId: string | null;
  decisionType: string | null;
  decisionSubtype: string | null;
  systemClassifiedType: string | null;
  systemClassifiedSubtype: string | null;
  classificationConfidence: number | null;
  classificationOverridden: boolean;
  classificationInputHash: string | null;
  classificationModel: string | null;
  classificationSignals: string[];
  preliminaryImpactLevel: string | null;
  affectedRoles: string[];
  sessionTitle: string;
  trigger: string;
  optionA: string;
  optionB: string;
  decisionScope: string;
  stakes: string;
  stakesWrongChoice: string;
  stakesWrongChoiceA: string;
  stakesWrongChoiceB: string;
  reversibilityA: string;
  reversibilityB: string;
  stakesTimeframeA: string;
  stakesTimeframeB: string;
  stakesInaction: string;
  stakesWorstCase: string;
  stakesWorstCaseReason: string;
  stakesWorstCaseTrigger: string;
  stakesWorstCaseProbability: string;
  stakesTimeframe: string;
  stakesInactionTimeframe: string;
  previousAttempts: string;
  impactScore: number;
  urgencyScore: number;
  impactMetric: string;
  urgencyReason: string;
  initialPreference: 'a' | 'b' | 'undecided';
  pendingInvites: PendingInvite[];
  brandingData: BrandingData | null;
  // Step 2.3: Structured onboarding fields
  timeHorizonDays: number | null;
  riskToleranceLevel: 'conservative' | 'moderate' | 'aggressive' | null;
  successDefinition: string;
  primaryMetric: string;
  
  setStep: (step: number) => void;
  setSubStep: (subStep: number) => void;
  setWorkspace: (id: string) => void;
  setDecisionArea: (type: string) => void;
  setDecisionSubtype: (subtype: string) => void;
  setClassificationSource: (systemType: string, systemSubtype: string, confidence: number, inputHash?: string | null, model?: string | null) => void;
  setClassificationOverridden: (overridden: boolean) => void;
  setClassificationSignals: (signals: string[]) => void;
  setPreliminaryImpactLevel: (level: string | null) => void;
  setAffectedRoles: (roles: string[]) => void;
  setSessionTitle: (title: string) => void;
  setTrigger: (trigger: string) => void;
  setOptionA: (val: string) => void;
  setOptionB: (val: string) => void;
  setDecisionScope: (val: string) => void;
  setStructuredTrigger: (optionA: string, optionB: string) => void;
  setInitialPreference: (pref: 'a' | 'b' | 'undecided') => void;
  setStakes: (stakes: string) => void;
  setStructuredStakes: (data: { wrongChoiceA: string; wrongChoiceB: string; reversibilityA: string; reversibilityB: string; timeframeA: string; timeframeB: string; inaction: string; inactionTimeframe: string; worstCase: string; worstCaseReason: string; worstCaseTrigger: string; worstCaseProbability: string }) => void;
  setPreviousAttempts: (attempts: string) => void;
  setSituation: (trigger: string, stakes: string, previousAttempts: string) => void;
  setScores: (impact: number, urgency: number, impactMetric?: string, urgencyReason?: string) => void;
  setImpactMetric: (metric: string) => void;
  setUrgencyReason: (reason: string) => void;
  setPendingInvites: (invites: PendingInvite[]) => void;
  setBrandingData: (data: BrandingData) => void;
  setTimeHorizonDays: (days: number | null) => void;
  setRiskToleranceLevel: (level: 'conservative' | 'moderate' | 'aggressive' | null) => void;
  setSuccessDefinition: (def: string) => void;
  setPrimaryMetric: (metric: string) => void;
  reset: () => void;
}

export type { PendingInvite, BrandingData };

const initialState = {
  currentStep: 1,
  currentSubStep: 0,
  workspaceId: null,
  decisionType: null,
  decisionSubtype: null,
  systemClassifiedType: null as string | null,
  systemClassifiedSubtype: null as string | null,
  classificationConfidence: null as number | null,
  classificationOverridden: false,
  classificationInputHash: null as string | null,
  classificationModel: null as string | null,
  classificationSignals: [] as string[],
  preliminaryImpactLevel: null as string | null,
  affectedRoles: [] as string[],
  sessionTitle: '',
  trigger: '',
  optionA: '',
  optionB: '',
  decisionScope: '',
  stakes: '',
  stakesWrongChoice: '',
  stakesWrongChoiceA: '',
  stakesWrongChoiceB: '',
  reversibilityA: '',
  reversibilityB: '',
  stakesTimeframeA: '',
  stakesTimeframeB: '',
  stakesInaction: '',
  stakesWorstCase: '',
  stakesWorstCaseReason: '',
  stakesWorstCaseTrigger: '',
  stakesWorstCaseProbability: '',
  stakesTimeframe: '',
  stakesInactionTimeframe: '',
  previousAttempts: '',
  impactScore: 0,
  urgencyScore: 0,
  impactMetric: '',
  urgencyReason: '',
  initialPreference: 'undecided' as 'a' | 'b' | 'undecided',
  pendingInvites: [] as PendingInvite[],
  brandingData: null as BrandingData | null,
  timeHorizonDays: null as number | null,
  riskToleranceLevel: null as 'conservative' | 'moderate' | 'aggressive' | null,
  successDefinition: '',
  primaryMetric: '',
};

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      ...initialState,
      
      setStep: (step) => set({ currentStep: step, currentSubStep: 0 }),
      setSubStep: (subStep) => set({ currentSubStep: subStep }),
      setWorkspace: (id) => set({ workspaceId: id }),
      setDecisionArea: (type) => set({ decisionType: type }),
      setDecisionSubtype: (subtype) => set({ decisionSubtype: subtype }),
      setClassificationSource: (systemType, systemSubtype, confidence, inputHash, model) =>
        set({ systemClassifiedType: systemType, systemClassifiedSubtype: systemSubtype, classificationConfidence: confidence, classificationInputHash: inputHash ?? null, classificationModel: model ?? null }),
      setClassificationOverridden: (overridden) => set({ classificationOverridden: overridden }),
      setClassificationSignals: (signals) => set({ classificationSignals: signals }),
      setPreliminaryImpactLevel: (level) => set({ preliminaryImpactLevel: level }),
      setAffectedRoles: (roles) => set({ affectedRoles: roles }),
      setSessionTitle: (title) => set({ sessionTitle: title }),
      setTrigger: (trigger) => set({ trigger }),
      setOptionA: (val) => set({ optionA: val }),
      setOptionB: (val) => set({ optionB: val }),
      setDecisionScope: (val) => set({ decisionScope: val }),
      setStructuredTrigger: (optionA, optionB) => {
        const trigger = `Option A: ${optionA.trim()} | Option B: ${optionB.trim()}`;
        set({ optionA, optionB, trigger });
      },
      setInitialPreference: (pref) => set({ initialPreference: pref }),
      setStakes: (stakes) => set({ stakes }),
      setStructuredStakes: (data) => {
        const parts = [
          data.wrongChoiceA.trim() ? `Option A falsch: ${data.wrongChoiceA.trim()} (${data.timeframeA}M, Rev: ${data.reversibilityA || '?'})` : '',
          data.wrongChoiceB.trim() ? `Option B falsch: ${data.wrongChoiceB.trim()} (${data.timeframeB}M, Rev: ${data.reversibilityB || '?'})` : '',
          data.inaction.trim() ? `Nicht-Entscheidung (${data.inactionTimeframe || '?'}M): ${data.inaction.trim()}` : '',
          data.worstCase.trim() ? `Worst Case: ${data.worstCase.trim()}` : '',
        ].filter(Boolean);
        const stakes = parts.join(' | ');
        set({
          stakesWrongChoiceA: data.wrongChoiceA, stakesWrongChoiceB: data.wrongChoiceB,
          stakesWrongChoice: `${data.wrongChoiceA} | ${data.wrongChoiceB}`,
          reversibilityA: data.reversibilityA, reversibilityB: data.reversibilityB,
          stakesTimeframeA: data.timeframeA, stakesTimeframeB: data.timeframeB,
          stakesTimeframe: data.timeframeA,
          stakesInaction: data.inaction, stakesInactionTimeframe: data.inactionTimeframe,
          stakesWorstCase: data.worstCase, stakesWorstCaseReason: data.worstCaseReason, stakesWorstCaseTrigger: data.worstCaseTrigger ?? '', stakesWorstCaseProbability: data.worstCaseProbability ?? '',
          stakes,
        });
      },
      setPreviousAttempts: (attempts) => set({ previousAttempts: attempts }),
      setSituation: (trigger, stakes, previousAttempts) => 
        set({ trigger, stakes, previousAttempts }),
      setScores: (impact, urgency, impactMetric, urgencyReason) => set({ 
        impactScore: impact, urgencyScore: urgency,
        ...(impactMetric !== undefined && { impactMetric }),
        ...(urgencyReason !== undefined && { urgencyReason }),
      }),
      setImpactMetric: (metric) => set({ impactMetric: metric }),
      setUrgencyReason: (reason) => set({ urgencyReason: reason }),
      setPendingInvites: (invites) => set({ pendingInvites: invites }),
      setBrandingData: (data) => set({ brandingData: data }),
      setTimeHorizonDays: (days) => set({ timeHorizonDays: days }),
      setRiskToleranceLevel: (level) => set({ riskToleranceLevel: level }),
      setSuccessDefinition: (def) => set({ successDefinition: def }),
      setPrimaryMetric: (metric) => set({ primaryMetric: metric }),
      reset: () => set(initialState),
    }),
    {
      name: 'quentari-onboarding',
      version: 15,
      migrate: (persistedState: any, version: number) => {
        // Always merge with initialState to ensure new fields exist
        return { ...initialState, ...persistedState };
      },
    }
  )
);
